import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BoxV4, TextInputV4 } from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { Grid } from '@mui/material'
import { debounce, get } from 'lodash'
import { useCallback } from 'react'
import { MicrosoftResellerDetails } from '../../../../types/resaleAuthorization'
import {
  MicrosoftFormFAQs,
  MicrosoftFormFAQsItem,
} from '../MicrosoftFormFAQs/MicrosoftFormFAQs'
import { useSelector } from '../../../../store'
import {
  FieldMetadata,
  FieldMetadataField,
} from '../../../../modules/privateOffer/types'
import { Product } from '../../../../modules/productsListing/types'

export const ResellerInfo = ({ product }: { product: Product }) => {
  const { values, setFieldValue, touched, errors, handleBlur } =
    useFormikContext() as FormikProps<Record<string, unknown>>

  const resellerDetails: MicrosoftResellerDetails[] =
    (values?.resellerDetails as MicrosoftResellerDetails[]) || []

  const fieldMetadata = useSelector(
    state =>
      state.resaleAuthorizationsInCreation
        ?.currentResaleAuthorizationInCreation?.[product.productId]
        ?.fieldMetadata
  ) as FieldMetadata | undefined

  const isResellerNameHidden = (
    fieldMetadata?.resellerName as FieldMetadataField
  )?.hidden

  const isReadOnlyResellerName = (
    fieldMetadata?.resellerName as FieldMetadataField
  )?.readOnly

  const isReadOnlyResellerAccountId = (
    fieldMetadata?.resellerAccountId as FieldMetadataField
  )?.readOnly

  const isResellerAccountIdHidden = (
    fieldMetadata?.resellerAccountId as FieldMetadataField
  )?.hidden

  const debouncedSetFieldValue = useCallback(
    debounce((name, value) => {
      setFieldValue(name, value)
    }, 500),
    []
  )

  const formChangeHandler = useCallback(
    (keyName: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        debouncedSetFieldValue(keyName, event.target.value)
      },
    [debouncedSetFieldValue]
  )

  return (
    <BoxV4
      heading={'Reseller details'}
      headingIcon={
        <FontAwesomeIcon
          icon={faListCheck}
          style={{ width: '16px', height: '16px' }}
        />
      }
      secondaryActions={
        <MicrosoftFormFAQs item={MicrosoftFormFAQsItem.RESALE_INFO} />
      }
    >
      <Grid container spacing={3} width={'100%'}>
        {!isResellerNameHidden && (
          <Grid size={6}>
            <TextInputV4
              keyName="resellerDetails.0.partnerName"
              title="Reseller name"
              type="text"
              placeHolder=""
              value={get(resellerDetails, '[0].partnerName', '')}
              isError={
                !!(
                  get(touched, 'resellerDetails[0].partnerName', false) &&
                  get(errors, 'resellerDetails[0].partnerName', false)
                )
              }
              onChange={formChangeHandler('resellerDetails[0].partnerName')}
              isReadOnly={isReadOnlyResellerName}
              isRequired={false}
              errorText={
                (!!get(touched, 'resellerDetails[0].partnerName', false) &&
                  get(errors, 'resellerDetails[0].partnerName', '')) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
              showInsideInfoIcon={true}
              showInsideInfoIconTooltip={{
                content: 'Enter the full legal company name of the Reseller.',
              }}
            />
          </Grid>
        )}
        {!isResellerAccountIdHidden && (
          <Grid size={6}>
            <TextInputV4
              keyName="resellerDetails.0.id"
              title="Reseller account ID"
              type="number"
              placeHolder=""
              value={get(resellerDetails, '[0].id', '')}
              isError={
                !!(
                  get(touched, 'resellerDetails[0].id', false) &&
                  get(errors, 'resellerDetails[0].id', false)
                )
              }
              onChange={formChangeHandler('resellerDetails.[0].id')}
              isReadOnly={isReadOnlyResellerAccountId}
              isRequired={false}
              errorText={
                (!!get(touched, 'resellerDetails[0].id', false) &&
                  get(errors, 'resellerDetails[0].id', '')) ||
                ''
              }
              showSpinner={false}
              onBlur={handleBlur}
              showInsideInfoIcon={true}
              showInsideInfoIconTooltip={{
                content:
                  'Microsoft Partner Center ID of the Reseller. This must be a valid and authorized Reseller under Microsoft.',
              }}
            />
          </Grid>
        )}
      </Grid>
    </BoxV4>
  )
}
